import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Text, MessageBar, MessageBarBody, MessageBarActions } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import { BasicButton } from '@components/common/BasicButton';
import { SpaceCard } from '@components/spaces/SpaceCard';
import { SpaceCardSkeleton } from '@components/spaces/SpaceCardSkeleton';
import { SpaceFilterToggle } from '@/components/spaces/SpaceFilterToggle';
import { SpaceAmenityFilter } from '@/components/spaces/SpaceAmenityFilter';
import { InlineStepActions } from '@components/common/InlineStepActions';
import { LoadSentinel } from '@components/common/InfiniteScroll';
import { useDelayedFlag } from '@/hooks/useDelayedFlag';
import { getRecommendedSpaceIds } from '@/utils/spaceRecommendation';
import { useSpaceSelectionStyles } from './SpaceSelection.styles';
import type { SpaceWithAvailability } from '@store/models';

const INITIAL_SHIMMER_COUNT = 10;
const INCREMENTAL_SHIMMER_COUNT = 5;
const SHIMMER_FLICKER_DELAY_MS = 100;

export interface SpaceSelectionProps {
  spaces: SpaceWithAvailability[];
  loading: boolean;
  error?: string | null;
  selectedSpaceId?: string | null;
  attendeeCount?: number;
  selectedSetups?: string[];
  hasMore?: boolean;
  isLoadingMore?: boolean;
  batchError?: string | null;
  onSelectSpace: (spaceId: string) => void;
  onSetupChange?: (spaceId: string, setupCodes: string[]) => void;
  onRetry?: () => void;
  onLoadMore?: () => void;
  onRetryBatch?: () => void;
  onAmenityFilterChange?: (amenities: string[]) => void;
  /** Optional callbacks to enable the inline Continue/Save-as-draft pair under the selected card. */
  onInlineContinue?: () => void;
  onInlineSaveAsDraft?: () => void;
  /** When true, the inline Continue button is disabled (e.g., preferences pending). */
  inlineContinueDisabled?: boolean;
  /** When true, the inline Save-as-draft button is disabled. */
  inlineSaveAsDraftDisabled?: boolean;
}

export const SpaceSelection: React.FC<SpaceSelectionProps> = ({
  spaces,
  loading,
  error,
  selectedSpaceId,
  attendeeCount,
  selectedSetups,
  hasMore,
  isLoadingMore,
  batchError,
  onSelectSpace,
  onSetupChange,
  onRetry,
  onLoadMore,
  onRetryBatch,
  onAmenityFilterChange,
  onInlineContinue,
  onInlineSaveAsDraft,
  inlineContinueDisabled = false,
  inlineSaveAsDraftDisabled = false,
}) => {
  const { t } = useTranslation('bookingForm');
  const styles = useSpaceSelectionStyles();
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [selectedAmenities, setSelectedAmenities] = useState<Set<string>>(new Set());
  const initialAmenityCodesRef = useRef<string[]>([]);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const showInitialShimmer = useDelayedFlag(loading, SHIMMER_FLICKER_DELAY_MS);
  const showIncrementalShimmer = useDelayedFlag(Boolean(isLoadingMore), SHIMMER_FLICKER_DELAY_MS);

  // Derive known amenity codes from i18n resource bundle, merge with dynamic codes from spaces
  const allAmenityCodes = useMemo(() => {
    const amenityTranslations = t('bookingForm.spaceFilter.amenity', { returnObjects: true });
    const knownCodes =
      typeof amenityTranslations === 'object' && amenityTranslations
        ? Object.keys(amenityTranslations as Record<string, string>)
        : [];
    const codes = new Set<string>(knownCodes);
    for (const space of spaces) {
      if (space.amenityCodes) {
        for (const code of space.amenityCodes) {
          codes.add(code.toUpperCase());
        }
      }
    }
    return Array.from(codes).sort();
  }, [spaces, t]);

  // Capture the amenity list once from the first non-empty load so checkboxes stay stable
  useEffect(() => {
    if (initialAmenityCodesRef.current.length === 0 && allAmenityCodes.length > 0) {
      initialAmenityCodesRef.current = allAmenityCodes;
    }
  }, [allAmenityCodes]);

  const stableAmenityCodes =
    initialAmenityCodesRef.current.length > 0 ? initialAmenityCodesRef.current : allAmenityCodes;

  // Compute which spaces should display the "Recommended" badge based on the
  // attendee count and a +10 capacity buffer. See `getRecommendedSpaceIds`
  // for the full algorithm (in-range matches win; otherwise the smallest
  // capacity strictly above the range is the fallback).
  const recommendedSpaceIds = useMemo(
    () => getRecommendedSpaceIds(spaces, attendeeCount),
    [spaces, attendeeCount]
  );

  // Move the single recommended space to the top of the list so it's
  // immediately visible without scrolling.
  const sortedSpaces = useMemo(() => {
    if (recommendedSpaceIds.size === 0) return spaces;
    const recommended: SpaceWithAvailability[] = [];
    const rest: SpaceWithAvailability[] = [];
    for (const space of spaces) {
      if (recommendedSpaceIds.has(space.spaceId)) {
        recommended.push(space);
      } else {
        rest.push(space);
      }
    }
    return [...recommended, ...rest];
  }, [spaces, recommendedSpaceIds]);

  const handleToggleAmenity = useCallback(
    (code: string) => {
      setSelectedAmenities((prev) => {
        const next = new Set(prev);
        if (next.has(code)) {
          next.delete(code);
        } else {
          next.add(code);
        }
        // Debounce the API call by 1000ms
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
        const amenityArray = Array.from(next);
        debounceTimerRef.current = setTimeout(() => {
          onAmenityFilterChange?.(amenityArray);
        }, 1000);
        return next;
      });
    },
    [onAmenityFilterChange]
  );

  const handleResetAmenities = useCallback(() => {
    setSelectedAmenities(new Set());
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    onAmenityFilterChange?.([]);
  }, [onAmenityFilterChange]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Show early returns only when filter panel has never been opened (initial load states)
  const hasFilterContext =
    selectedAmenities.size > 0 || filterPanelOpen || stableAmenityCodes.length > 0;

  if (loading && !hasFilterContext) {
    return (
      <div
        role="status"
        aria-live="polite"
        aria-label={t('bookingForm.space.loadingMore')}
        data-testid="space-selection-initial-shimmer"
      >
        {showInitialShimmer && (
          <div className={styles.grid} role="list">
            {Array.from({ length: INITIAL_SHIMMER_COUNT }, (_, i) => (
              <SpaceCardSkeleton key={i} />
            ))}
          </div>
        )}
      </div>
    );
  }

  if (error && !hasFilterContext) {
    return (
      <div className={styles.errorContainer} data-testid="space-selection-error">
        <Text className={styles.errorText}>{error}</Text>
        {onRetry && (
          <BasicButton variant="primary" onClick={onRetry}>
            {t('bookingForm.space.retry')}
          </BasicButton>
        )}
      </div>
    );
  }

  if (spaces.length === 0 && selectedAmenities.size === 0 && !hasFilterContext) {
    return (
      <div className={styles.emptyContainer}>
        <Text>{t('bookingForm.space.noSpacesAvailable')}</Text>
      </div>
    );
  }

  return (
    <div ref={scrollContainerRef} className={styles.root}>
      <div className={styles.subtitleRow}>
        <Text className={styles.resultCount} data-testid="space-results-count">
          {!loading && !error
            ? t('bookingForm.spaceFilter.showingResults', { count: spaces.length })
            : ''}
        </Text>
        <SpaceFilterToggle
          expanded={filterPanelOpen}
          onClick={() => setFilterPanelOpen((prev) => !prev)}
        />
      </div>
      {filterPanelOpen && (
        <SpaceAmenityFilter
          amenityCodes={stableAmenityCodes}
          selectedAmenities={selectedAmenities}
          onToggle={handleToggleAmenity}
          onReset={handleResetAmenities}
        />
      )}
      {loading ? (
        <div
          role="status"
          aria-live="polite"
          aria-label={t('bookingForm.space.loadingMore')}
          data-testid="space-selection-initial-shimmer"
        >
          {showInitialShimmer && (
            <div className={styles.grid} role="list">
              {Array.from({ length: INITIAL_SHIMMER_COUNT }, (_, i) => (
                <SpaceCardSkeleton key={i} />
              ))}
            </div>
          )}
        </div>
      ) : error ? (
        <div className={styles.errorContainer} data-testid="space-selection-error">
          <Text className={styles.errorText}>{error}</Text>
          {onRetry && (
            <BasicButton variant="primary" onClick={onRetry}>
              {t('bookingForm.space.retry')}
            </BasicButton>
          )}
        </div>
      ) : spaces.length === 0 && selectedAmenities.size === 0 ? (
        <div className={styles.emptyContainer}>
          <Text>{t('bookingForm.space.noSpacesAvailable')}</Text>
        </div>
      ) : (
        <>
          {spaces.length === 0 ? (
            <div className={styles.emptyContainer}>
              <Text>{t('bookingForm.spaceFilter.noMatch')}</Text>
            </div>
          ) : (
            <div className={styles.grid} role="list">
              {sortedSpaces.map((space) => {
                const isSelected = space.spaceId === selectedSpaceId;
                const inlineEnabled = Boolean(onInlineContinue && onInlineSaveAsDraft);
                return (
                  <div key={space.spaceId} role="listitem">
                    <SpaceCard
                      space={space}
                      selected={isSelected}
                      {...(attendeeCount !== undefined && { attendeeCount })}
                      {...(selectedSetups && isSelected ? { selectedSetups } : {})}
                      isRecommended={recommendedSpaceIds.has(space.spaceId)}
                      onSelect={onSelectSpace}
                      onSetupChange={onSetupChange}
                    />
                    {inlineEnabled && (
                      <InlineStepActions
                        visible={isSelected}
                        continueDisabled={inlineContinueDisabled}
                        saveAsDraftDisabled={inlineSaveAsDraftDisabled}
                        onContinue={onInlineContinue ?? (() => undefined)}
                        onSaveAsDraft={onInlineSaveAsDraft ?? (() => undefined)}
                        continueLabel={t('bookingForm.buttons.next')}
                        saveAsDraftLabel={t('bookingForm.buttons.saveAsDraft')}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
      <div
        role="status"
        aria-live="polite"
        aria-label={t('bookingForm.space.loadingMore')}
        data-testid="space-selection-incremental-shimmer"
      >
        {showIncrementalShimmer && (
          <div className={styles.grid}>
            {Array.from({ length: INCREMENTAL_SHIMMER_COUNT }, (_, i) => (
              <SpaceCardSkeleton key={i} />
            ))}
          </div>
        )}
      </div>
      {hasMore && onLoadMore && (
        <LoadSentinel
          onReachEnd={onLoadMore}
          disabled={!hasMore || Boolean(isLoadingMore)}
          rootRef={scrollContainerRef}
        />
      )}
      {batchError && (
        <MessageBar intent="error" data-testid="space-selection-batch-error">
          <MessageBarBody>{batchError}</MessageBarBody>
          {onRetryBatch && (
            <MessageBarActions>
              <BasicButton onClick={onRetryBatch} variant="transparent">
                {t('bookingForm.space.batchRetry')}
              </BasicButton>
            </MessageBarActions>
          )}
        </MessageBar>
      )}
    </div>
  );
};

SpaceSelection.displayName = 'SpaceSelection';
